/**
 * Tab title
 */

import config from 'config';
import {
  AUTH,
  BUY_PRODUCT,
  BUY_PRODUCT_CANCEL,
  BUY_PRODUCT_PROCESSING,
  BUY_PRODUCT_SUCCESS,
  BUY_SECONDHAND_FASHION,
  BUY_SECONDHAND_FASHION2,
  CATEGORY,
  CATEGORY_PRODUCTS,
  CHECKOUT_CANCEL,
  CHECKOUT_SUCCESS,
  FAQ,
  getRouteLanguages,
  INDEX,
  MY,
  NOTIFICATIONS,
  PRODUCT,
  PRODUCT_EDIT,
  PRODUCTS,
  SEARCH,
  SELL_ADD_PRODUCT,
  SELL_ADD_PRODUCT_SUCCESS,
  SHOP,
  SHOPS,
  START_EARNING_EXTRA,
  START_EARNING_EXTRA2,
} from 'config/fe-routes';
import Head from 'next/head';
import Router from 'next/router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'utils/dom-element-helpers';

const tabTitlePrefix = config.get('web.tabTitlePrefix') || '';

type BreadcrumbPropsType = {
  folderPath: string;
};

const TabTitle = (props: BreadcrumbPropsType) => {
  const { folderPath } = props;
  const { t } = useTranslation();
  const query: any = Router?.router?.query;
  const routeLanguages = getRouteLanguages();
  const allFERouters = config.get('feRoutes');

  // Define tab titles for all route languages
  const tabTitles = routeLanguages.reduce(
    (allTabTitles, routeLanguageCode) => ({
      ...allTabTitles,

      [INDEX().href]: {
        title: t('pages.index.title'),
      },

      [SEARCH('', routeLanguageCode).href]: {
        title: t('components.search-bar.title'),
      },

      [SHOPS(routeLanguageCode).href]: {
        title: t('pages.shops.title'),
      },

      [SHOP('').href]: {
        title: query?.['shop-slug'] ? '@' + query?.['shop-slug'] : t('common.shop'),
        capitalize: false,
      },
      '/[shop-slug]/product': {
        title: query?.['shop-slug'] ? '@' + query?.['shop-slug'] : t('common.shop'),
        capitalize: false,
      },
      '/[shop-slug]/toode': {
        title: query?.['shop-slug'] ? '@' + query?.['shop-slug'] : t('common.shop'),
        capitalize: false,
      },

      [PRODUCT('', '', routeLanguageCode).href]: {
        title: (query?.['shop-slug'] ? '@' + query?.['shop-slug'] : '') + ' ' + t('common.product'),
        capitalize: false,
      },

      [PRODUCTS(routeLanguageCode).href]: {
        title: t('pages.products.title'),
      },

      [PRODUCT_EDIT('', '', routeLanguageCode).href]: {
        title: t('components.product-edit.title'),
        capitalize: false,
      },

      [NOTIFICATIONS(routeLanguageCode).href]: {
        title: t('pages.notifications.title'),
      },

      [CATEGORY('').href]: {
        title: query?.['category-name']?.replace(/-/gim, ' ') || t('common.category'),
        capitalize: true,
      },

      [CATEGORY_PRODUCTS('').href]: {
        title: query?.['category-name']?.replace(/-/gim, ' ') || t('common.category'),
        capitalize: true,
      },

      [SELL_ADD_PRODUCT(routeLanguageCode).href]: {
        title: t('pages.sell.add-product.title'),
      },

      [BUY_PRODUCT('', '', routeLanguageCode).href]: {
        title: t('pages.buy.title'),
      },

      [BUY_PRODUCT_SUCCESS('', '', routeLanguageCode).href]: {
        title: t('pages.buy-success.title'),
      },

      [BUY_PRODUCT_CANCEL('', '', routeLanguageCode).href]: {
        title: t('pages.buy-cancel.title'),
      },

      [BUY_PRODUCT_PROCESSING('', '', routeLanguageCode).href]: {
        title: t('common.processing-order'),
      },

      [FAQ(routeLanguageCode).href]: {
        title: t('pages.faq.title'),
      },

      [START_EARNING_EXTRA(routeLanguageCode).href]: {
        title: t('pages.start-earning-extra.title'),
      },
      [BUY_SECONDHAND_FASHION(routeLanguageCode).href]: {
        title: t('pages.buy-secondhand-fashion.title'),
      },
      [START_EARNING_EXTRA2(routeLanguageCode).href]: {
        title: t('pages.start-earning-extra.title'),
      },
      [BUY_SECONDHAND_FASHION2(routeLanguageCode).href]: {
        title: t('pages.buy-secondhand-fashion.title'),
      },

      [MY.ORDER(0, routeLanguageCode).href]: {
        title: t('pages.my-order.title') + ' ' + (query?.['order-id'] ? `#${query?.['order-id']}` : ''),
      },
      '/user/order': {
        title: t('pages.my-order.title') + ' ' + (query?.['order-id'] ? `#${query?.['order-id']}` : ''),
      },
      '/user/order/[order-id]': {
        title: t('pages.my-order.title') + ' ' + (query?.['order-id'] ? `#${query?.['order-id']}` : ''),
      },

      [MY.ORDERS(routeLanguageCode).href]: {
        title: t('pages.orders.title'),
      },

      [MY.CONVERSATION_WITH_PRODUCT('', '', routeLanguageCode).href]: {
        title: t('pages.conversation-with-product.title'),
      },

      [MY.CONVERSATION('', routeLanguageCode).href]: {
        title: t('pages.conversation.title'),
      },

      [MY.SHOP(routeLanguageCode).href]: {
        title: t('pages.shop.title'),
      },

      [MY.DISCOUNT_CODES(routeLanguageCode).href]: {
        title: t('pages.discount-codes.title'),
      },

      [MY.PURCHASE(0, routeLanguageCode).href]: {
        title: t('pages.purchase.title') + ' ' + (query?.['purchase-id'] ? `#${query?.['purchase-id']}` : ''),
      },
      '/user/purchase/[purchase-id]': {
        title: t('pages.purchase.title') + ' ' + (query?.['purchase-id'] ? `#${query?.['purchase-id']}` : ''),
      },
      '/user/purchase': {
        title: t('pages.purchase.title') + ' ' + (query?.['purchase-id'] ? `#${query?.['purchase-id']}` : ''),
      },

      [MY.PURCHASES(routeLanguageCode).href]: {
        title: t('pages.purchases.title'),
      },
      '/user/purchases': {
        title: t('pages.purchases.title'),
      },

      [MY.WALLET(routeLanguageCode).href]: {
        title: t('pages.wallet.title'),
      },

      [MY.SETTINGS(routeLanguageCode).href]: {
        title: t('pages.settings.title'),
      },
      [MY.SETTINGS_PROFILE(routeLanguageCode).href]: {
        title: t('pages.settings.profile.title'),
      },
      [MY.SETTINGS_BANK(routeLanguageCode).href]: {
        title: t('pages.settings.profile-bank.title'),
      },
      [MY.SETTINGS_NOTIFICATIONS(routeLanguageCode).href]: {
        title: t('pages.settings.profile-notifications.title'),
      },

      [MY.MESSAGES(routeLanguageCode).href]: {
        title: t('pages.messages.title'),
      },

      [MY.PROMOCODES(routeLanguageCode).href]: {
        title: t('pages.promocodes.title'),
      },
      [MY.PROMOCODES_ENTERCODE(routeLanguageCode).href]: {
        title: t('pages.promocodes.entercode.title'),
      },
      [MY.PROMOCODES_INVITE(routeLanguageCode).href]: {
        title: t('pages.invite-friends.referral.title'),
      },

      [SELL_ADD_PRODUCT_SUCCESS('', routeLanguageCode).href]: {
        title: t('common.product-added'),
      },
      [CHECKOUT_SUCCESS().href]: {
        title: t('pages.buy-success.title'),
      },
      [CHECKOUT_CANCEL().href]: {
        title: t('pages.buy-cancel.title'),
      },
      '/checkout/cancel/[shop-slug]': {
        title: t('pages.buy-success.title'),
      },
      '/checkout/processing': {
        title: t('common.processing-order'),
      },
      '/checkout/cancel/[shop-slug]/[product-slug]': {
        title: t('pages.buy-cancel.title'),
      },
      '/seller/start': {
        title: t('common.start-selling'),
      },
      '/category': {
        title: t('pages.category.title'),
      },
      '/kategooria': {
        title: t('pages.category.title'),
      },
      '/messages': {
        title: t('pages.messages.title'),
      },
      [SEARCH(query?.search || '', routeLanguageCode).href]: {
        title: t('pages.search.title') + (query?.search ? ` - ${query?.search}` : ''),
      },
      [SEARCH('search', routeLanguageCode).href]: {
        title: t('pages.search.title') + (query?.search ? ` - ${query?.search}` : ''),
      },
      [AUTH().href]: {
        title: t('common.logging-in'),
      },
      '/settings': {
        title: t('pages.settings.title'),
      },
      '/404 ': {
        title: t('common.general-error'),
      },
      '/seller': {
        title: '-',
      },
      '/user': {
        title: '-',
      },
      '/minu': {
        title: '-',
      },
      '/my': {
        title: '-',
      },
      '/checkout': {
        title: '-',
      },
      '/t3st': {
        title: 'Test',
      },
    }),
    {},
  );

  // Check if all routes have tab title
  useEffect(() => {
    allFERouters?.forEach((route) => {
      if (!tabTitles[route]?.title) {
        console.warn(`Missing tab title for route ${route}:`, tabTitles[route]);
      }
      if (['pages.', 'common.', 'components.'].some((key) => tabTitles[route]?.title?.includes(key))) {
        console.warn(`Untranslated tab title for route ${route}?:`, tabTitles[route]);
      }
    });
  }, [allFERouters]);

  // Get current tab title from our tab titles list
  const tabTitle = tabTitles[folderPath];
  const displayTitle = tabTitle?.title // Do not show undefined in tab title
    ? tabTitle?.capitalize
      ? capitalize(tabTitle?.title)
      : tabTitle?.title
    : '';

  return (
    <Head>
      <title>{tabTitlePrefix + displayTitle}</title>
    </Head>
  );
};

export default TabTitle;
